import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

/** Data hooks for the Integrations Monitor (COPS-3). Every AI field is
 *  nullable by contract — the drawer renders pending states until step 5's
 *  triage/deep-review land. */

export type ReviewStatus = "pending" | "approved" | "flagged";
export type Cause =
  | "vendor_outage"
  | "vendor_data_quality"
  | "etl_bug"
  | "expected_change"
  | "holiday_seasonal"
  | "other";

export interface IncidentOut {
  id: string;
  feed: string;
  level: string;
  key_path: string;
  entity_source: string | null;
  company_id: string | null;
  company_name: string | null;
  location_id: string | null;
  location_name: string | null;
  severity: "critical" | "alert";
  trigger: string;
  opened_on: string;
  first_signal_on: string | null;
  last_bad_day: string | null;
  resolved_on: string | null;
  state: "open" | "resolved" | "dormant";
  streak_days: number;
  magnitude: number | null;
  snooze_until: string | null;
  reopened_count: number;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_superseded: boolean;
  cause: string | null;
  cause_detail: Record<string, unknown>;
  ai_verdict: string | null;
  ai_confidence: number | null;
  ai_reasoning: string | null;
  ai_model: string | null;
  ai_reviewed_at: string | null;
  deep_verdict: string | null;
  deep_reviewed_at: string | null;
  created_at: string;
}

/** State-qualified StatusBadge value: the solid chip is reserved for OPEN
 *  criticals (house chip-emphasis rule); dormant is its own quiet state. */
export function severityBadgeValue(r: Pick<IncidentOut, "severity" | "state">): string {
  if (r.state === "dormant") return "dormant";
  if (r.severity === "critical") return r.state === "open" ? "critical" : "critical_resolved";
  return r.state === "resolved" ? "resolved" : "alert";
}

export interface IncidentListOut {
  items: IncidentOut[];
  count: number;
  limit: number;
  offset: number;
}

export interface SignalOut {
  id: string;
  level: string;
  key_path: string;
  entity_source: string | null;
  company_name: string | null;
  location_name: string | null;
  event_date: string;
  last_age: number | null;
  expected_completeness: number | null;
  actual_at_age: number | null;
  baseline_median_at_age: number | null;
  shortfall_pct: number | null;
  confidence: "low" | "medium" | "high";
  outcome: string;
  cleared_reason: string | null;
  incident_id: string | null;
  last_evaluated_at: string | null;
}

export interface DeliveryRow {
  key_path: string;
  entity_source: string | null;
  state: string;
  last_arrival_at: string | null;
  expected_arrival_gap_h: number | null;
  lag_p50_h: number | null;
  maturity_age_days: number | null;
  profile_state: string;
}

export interface OverviewOut {
  healthy: number;
  open_incidents: number;
  critical_incidents: number;
  delivering_late: number;
  dormant: number;
  new_this_week: number;
  pending_signals: number;
  l1_open: number;
  l2_open: number;
  l3_open: number;
  digest: string | null;
  digest_run_date: string | null;
  signals: SignalOut[];
  delivery: DeliveryRow[];
  dormant_entities: IncidentOut[];
}

export interface TimeseriesPoint {
  day: string;
  event_count: number | null;
  arrival_count: number | null;
}

export interface TimeseriesOut {
  level: string;
  key_path: string;
  points: TimeseriesPoint[];
}

export interface LandscapePoint {
  level: string;
  key_path: string;
  entity_source: string | null;
  company_name: string | null;
  location_name: string | null;
  state: string;
  business: number;
  pipe: number;
  companies: number | null;
  volume_30d: number | null;
  components: Record<string, unknown>;
}

export interface DecompositionRow {
  company_id: string | null;
  company_name: string | null;
  baseline_avg: number;
  window_avg: number;
  shortfall_per_day: number;
}

export interface FirstSeenOut {
  id: string;
  level: string;
  key_path: string;
  entity_source: string | null;
  company_name: string | null;
  location_name: string | null;
  kind: string;
  detected_on: string;
  first_event_date: string | null;
  first_arrival_date: string | null;
  detail: {
    ramp?: number[];
    prior_rate?: number;
    recent_rate?: number;
    entity_ref?: string | null;
    entity_number?: string | null;
    inactive?: boolean;
  };
  annotation: string | null;
  acknowledged_at: string | null;
}

export interface WatchOut {
  id: string;
  feed: string;
  level: string;
  key_path: string;
  reason: string | null;
  expires_at: string | null;
  created_at: string;
  /** Readable identity, resolved server-side on every read. Names are best
   *  effort — fall back to the id when one comes back null. */
  entity_source: string;
  company_id: string | null;
  company_name: string | null;
  location_id: string | null;
  location_name: string | null;
  added_by_name: string | null;
}

export interface ThresholdOut {
  id: string;
  feed: string;
  level: string;
  key_path: string;
  z_threshold: number | null;
  materiality_floor: number | null;
  maturity_target_pct: number | null;
  budget_per_quarter: number | null;
  note: string | null;
  auto_tuned: boolean;
}

export interface ProfileOut {
  id: string;
  level: string;
  key_path: string;
  entity_source: string | null;
  state: string;
  maturity_age_days: number | null;
  lag_p50_h: number | null;
  lag_p95_h: number | null;
  healthy_ref: number | null;
  last_arrival_at: string | null;
  completeness_curve: Record<string, unknown>;
  profile_refreshed_at: string | null;
}

export interface RunLogOut {
  id: string;
  feed: string;
  run_date: string;
  kind: string;
  status: string | null;
  duration_ms: number | null;
  counts: Record<string, unknown>;
  error: string | null;
  created_at: string;
}

export interface DqEventOut {
  id: string;
  feed: string;
  entity_source: string | null;
  event_date: string | null;
  kind: string;
  magnitude_x: number | null;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface IncidentFilters {
  state: string;
  severity?: string;
  level?: string;
  entity_source?: string;
  opened_after?: string;
  opened_before?: string;
  limit: number;
  offset: number;
}

const KEYS = {
  overview: ["integrations", "overview"] as const,
  incidents: (f: IncidentFilters) => ["integrations", "incidents", f] as const,
  incident: (id: string) => ["integrations", "incident", id] as const,
};

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

export function useOverview() {
  return useQuery({
    queryKey: KEYS.overview,
    queryFn: () => apiFetch<OverviewOut>("/integrations/overview"),
    refetchInterval: 60_000,
  });
}

export function useIncidents(filters: IncidentFilters) {
  return useQuery({
    queryKey: KEYS.incidents(filters),
    queryFn: () =>
      apiFetch<IncidentListOut>(`/integrations/incidents${qs({ ...filters })}`),
  });
}

export function useIncident(id: string | null) {
  return useQuery({
    queryKey: KEYS.incident(id ?? ""),
    queryFn: () => apiFetch<IncidentOut>(`/integrations/incidents/${id}`),
    enabled: id != null,
  });
}

export function useIncidentDecomposition(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["integrations", "decomposition", id],
    queryFn: () =>
      apiFetch<DecompositionRow[]>(`/integrations/incidents/${id}/decomposition`),
    enabled: id != null && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useIncidentHistory(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["integrations", "history", id],
    queryFn: () => apiFetch<IncidentOut[]>(`/integrations/incidents/${id}/history`),
    enabled: id != null && enabled,
  });
}

export function useReviewIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      status: ReviewStatus;
      notes?: string | null;
      cause?: Cause | null;
      snooze_until?: string | null;
    }) =>
      apiFetch<IncidentOut>(`/integrations/incidents/${id}/review`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useTimeseries(level: string | null, keyPath: string | null, days: number) {
  return useQuery({
    queryKey: ["integrations", "timeseries", level, keyPath, days],
    queryFn: () =>
      apiFetch<TimeseriesOut>(
        `/integrations/entities/timeseries${qs({ level: level ?? "", key_path: keyPath ?? "", days })}`,
      ),
    enabled: level != null && keyPath != null,
    staleTime: 5 * 60_000,
  });
}

export interface ChildRow {
  level: string;
  key_path: string;
  child_id: string;
  name: string | null;
  volume_30d: number;
  prior_rate: number | null;
  recent_rate: number | null;
}

export interface EntityIdentityOut {
  level: string;
  key_path: string;
  entity_source: string;
  company_id: string | null;
  company_name: string | null;
  location_id: string | null;
  location_name: string | null;
}

/** Who a key IS. The drill drawer opens from every tab with nothing but
 *  (level, keyPath), so it asks rather than rendering the raw path. */
export function useEntityIdentity(level: string | null, keyPath: string | null) {
  return useQuery({
    queryKey: ["integrations", "entity-identity", level, keyPath],
    enabled: level != null && keyPath != null,
    queryFn: () =>
      apiFetch<EntityIdentityOut>(
        `/integrations/entities/identity?level=${encodeURIComponent(level as string)}` +
          `&key_path=${encodeURIComponent(keyPath as string)}`,
      ),
  });
}

export function useChildren(level: string | null, keyPath: string | null) {
  return useQuery({
    queryKey: ["integrations", "children", level, keyPath],
    queryFn: () =>
      apiFetch<ChildRow[]>(
        `/integrations/entities/children${qs({ level: level ?? "", key_path: keyPath ?? "" })}`,
      ),
    enabled: level != null && keyPath != null && (level === "L1" || level === "L2"),
    staleTime: 5 * 60_000,
  });
}

export function useLandscape(level: string, entitySource?: string) {
  return useQuery({
    queryKey: ["integrations", "landscape", level, entitySource],
    queryFn: () =>
      apiFetch<LandscapePoint[]>(
        `/integrations/landscape${qs({ level, entity_source: entitySource })}`,
      ),
  });
}

export function useNewEntities(days: number, kind?: string) {
  return useQuery({
    queryKey: ["integrations", "new-entities", days, kind],
    queryFn: () =>
      apiFetch<FirstSeenOut[]>(`/integrations/new-entities${qs({ days, kind })}`),
  });
}

export function useAckNewEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<FirstSeenOut>(`/integrations/new-entities/${id}/ack`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations", "new-entities"] });
    },
  });
}

export function useWatchlist() {
  return useQuery({
    queryKey: ["integrations", "watchlist"],
    queryFn: () => apiFetch<WatchOut[]>("/integrations/watchlist"),
  });
}

export function useAddWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      level: string;
      key_path: string;
      reason?: string;
      expires_at?: string;
    }) => apiFetch<WatchOut>("/integrations/watchlist", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations", "watchlist"] });
    },
  });
}

export function useRemoveWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/integrations/watchlist/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations", "watchlist"] });
    },
  });
}

export function useThresholds() {
  return useQuery({
    queryKey: ["integrations", "thresholds"],
    queryFn: () => apiFetch<ThresholdOut[]>("/integrations/thresholds"),
  });
}

export function usePatchThreshold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<ThresholdOut>) =>
      apiFetch<ThresholdOut>(`/integrations/thresholds/${id}`, { method: "PATCH", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations", "thresholds"] });
    },
  });
}

export function useCreateThreshold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      level: string;
      key_path: string;
      budget_per_quarter?: number;
      z_threshold?: number;
      materiality_floor?: number;
      note?: string;
    }) => apiFetch<ThresholdOut>("/integrations/thresholds", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations", "thresholds"] });
    },
  });
}

export function useProfiles() {
  return useQuery({
    queryKey: ["integrations", "profiles"],
    queryFn: () => apiFetch<ProfileOut[]>("/integrations/profiles"),
  });
}

export function useRuns() {
  return useQuery({
    queryKey: ["integrations", "runs"],
    queryFn: () => apiFetch<RunLogOut[]>("/integrations/runs"),
    refetchInterval: 15_000,
  });
}

export function useDqEvents() {
  return useQuery({
    queryKey: ["integrations", "dq-events"],
    queryFn: () => apiFetch<DqEventOut[]>("/integrations/dq-events"),
  });
}

export interface FeedDefinitionOut {
  id: string;
  feed: string;
  entity_source: string;
  delivery_mode: string;
  cadence: string;
  expected_interval_h: number | null;
  credential_grain: string;
  data_grain: string;
  monitor_l2_outages: boolean;
  monitor_l3_outages: boolean;
  notes: string | null;
  status: string;
  suggested_from: string | null;
  updated_at: string;
}

export function useFeedDefinitions() {
  return useQuery({
    queryKey: ["integrations", "feed-definitions"],
    queryFn: () => apiFetch<FeedDefinitionOut[]>("/integrations/feed-definitions"),
  });
}

export function usePatchFeedDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<FeedDefinitionOut>) =>
      apiFetch<FeedDefinitionOut>(`/integrations/feed-definitions/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations", "feed-definitions"] });
      void qc.invalidateQueries({
        queryKey: ["integrations", "feed-definition-changes"],
      });
    },
  });
}

export interface FeedDefinitionChangeOut {
  id: string;
  field: string;
  old_value: string | null;
  new_value: string | null;
  changed_by: string | null;
  changed_by_name: string | null;
  created_at: string;
}

export function useFeedDefinitionChanges(id: string | null) {
  return useQuery({
    queryKey: ["integrations", "feed-definition-changes", id],
    queryFn: () =>
      apiFetch<FeedDefinitionChangeOut[]>(`/integrations/feed-definitions/${id}/changes`),
    enabled: id != null,
  });
}

export function useTriggerRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: "nightly" | "replay"; lookback_days?: number }) =>
      apiFetch<{ status: string }>("/integrations/run", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations", "runs"] });
    },
  });
}
